import { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Webhook, Plus, Copy, Trash2, Send, Clock, CheckCircle, XCircle, Eye } from 'lucide-react';
import api from '../lib/api';

const AVAILABLE_EVENTS = [
    { value: 'order.created', label: 'Order Created', description: 'When a new order is placed' },
    { value: 'order.ready', label: 'Order Ready', description: 'When order is ready for pickup' },
    { value: 'order.completed', label: 'Order Completed', description: 'When order is delivered' },
    { value: 'item.out_of_stock', label: 'Out of Stock', description: 'When product inventory hits zero' },
    { value: 'inventory.updated', label: 'Inventory Updated', description: 'When stock levels change' },
];

export function WebhookManager() {
    const [webhooks, setWebhooks] = useState([]);
    const [deliveries, setDeliveries] = useState({});
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [newWebhook, setNewWebhook] = useState({ url: '', description: '', events: [] });
    const [showSecret, setShowSecret] = useState(null);
    const [selectedWebhook, setSelectedWebhook] = useState(null);

    useEffect(() => {
        fetchWebhooks();
    }, []);

    const fetchWebhooks = async () => {
        try {
            const response = await api.get('/webhooks');
            setWebhooks(response.data);
        } catch (error) {
            toast.error('Failed to load webhooks');
        } finally {
            setLoading(false);
        }
    };

    const fetchDeliveries = async (webhookId) => {
        try {
            const response = await api.get(`/webhooks/${webhookId}/deliveries`);
            setDeliveries(prev => ({ ...prev, [webhookId]: response.data }));
        } catch (error) {
            toast.error('Failed to load delivery history');
        }
    };

    const createWebhook = async () => {
        if (!newWebhook.url.trim()) {
            toast.error('Please enter a webhook URL');
            return;
        }
        if (newWebhook.events.length === 0) {
            toast.error('Please select at least one event');
            return;
        }

        setCreating(true);
        try {
            const response = await api.post('/webhooks', newWebhook);
            setShowSecret(response.data);
            setNewWebhook({ url: '', description: '', events: [] });
            fetchWebhooks();
            toast.success('Webhook created! Copy the secret now.');
        } catch (error) {
            toast.error('Failed to create webhook');
        } finally {
            setCreating(false);
        }
    };

    const deleteWebhook = async (id) => {
        if (!confirm('Delete this webhook?')) return;
        try {
            await api.delete(`/webhooks/${id}`);
            fetchWebhooks();
            toast.success('Webhook deleted');
        } catch (error) {
            toast.error('Failed to delete webhook');
        }
    };

    const testWebhook = async (id) => {
        try {
            await api.post(`/webhooks/${id}/test`);
            toast.success('Test webhook sent');
            setTimeout(() => fetchDeliveries(id), 2000);
        } catch (error) {
            toast.error('Failed to send test');
        }
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        toast.success('Copied to clipboard');
    };

    const toggleEvent = (event) => {
        setNewWebhook(prev => ({
            ...prev,
            events: prev.events.includes(event)
                ? prev.events.filter(e => e !== event)
                : [...prev.events, event]
        }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-8 w-8 border-2 border-cyan-500 border-t-transparent rounded-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Create New Webhook */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                        <Plus className="h-5 w-5" />
                        Add Webhook
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Webhook URL</label>
                        <Input
                            placeholder="https://partner.com/webhook"
                            value={newWebhook.url}
                            onChange={(e) => setNewWebhook(prev => ({ ...prev, url: e.target.value }))}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium">Description (optional)</label>
                        <Input
                            placeholder="e.g., DoorDash Integration"
                            value={newWebhook.description}
                            onChange={(e) => setNewWebhook(prev => ({ ...prev, description: e.target.value }))}
                        />
                    </div>

                    <div className="space-y-2">
                        <label className="text-sm font-medium">Events to Subscribe</label>
                        <div className="grid grid-cols-1 gap-2">
                            {AVAILABLE_EVENTS.map((event) => (
                                <label key={event.value} className="flex items-start gap-2 p-3 border rounded-lg cursor-pointer hover:bg-muted">
                                    <input
                                        type="checkbox"
                                        checked={newWebhook.events.includes(event.value)}
                                        onChange={() => toggleEvent(event.value)}
                                        className="mt-1"
                                    />
                                    <div>
                                        <p className="font-medium text-sm">{event.label}</p>
                                        <p className="text-xs text-muted-foreground">{event.description}</p>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>

                    <Button
                        onClick={createWebhook}
                        disabled={creating}
                        className="bg-cyan-500 hover:bg-cyan-600 text-white"
                    >
                        {creating ? 'Creating...' : 'Create Webhook'}
                    </Button>
                </CardContent>
            </Card>

            {/* Show Secret */}
            {showSecret && (
                <Card className="border-amber-500 bg-amber-50 dark:bg-amber-950/20">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-amber-700 dark:text-amber-400">
                            <Webhook className="h-5 w-5" />
                            Copy Your Webhook Secret
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <p className="text-sm text-amber-700 dark:text-amber-400">
                            This secret is used to verify webhook signatures. Copy it now - you won't see it again.
                        </p>
                        <div className="flex gap-2">
                            <code className="flex-1 p-3 bg-black text-green-400 font-mono text-sm rounded-lg break-all">
                                {showSecret.secret}
                            </code>
                            <Button variant="outline" onClick={() => copyToClipboard(showSecret.secret)}>
                                <Copy className="h-4 w-4" />
                            </Button>
                        </div>
                        <Button variant="outline" onClick={() => setShowSecret(null)} className="w-full">
                            I've Copied It
                        </Button>
                    </CardContent>
                </Card>
            )}

            {/* Webhooks List */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2 text-base">
                        <Webhook className="h-5 w-5" />
                        Active Webhooks ({webhooks.length})
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {webhooks.length === 0 ? (
                        <p className="text-muted-foreground text-center py-8">
                            No webhooks yet. Add one to receive real-time events.
                        </p>
                    ) : (
                        <div className="space-y-3">
                            {webhooks.map((webhook) => (
                                <div key={webhook.id} className="border rounded-lg p-4 space-y-3">
                                    <div className="flex items-start justify-between">
                                        <div className="space-y-1">
                                            <p className="font-medium">{webhook.description || webhook.url}</p>
                                            <p className="text-xs text-muted-foreground font-mono">{webhook.url}</p>
                                            <div className="flex gap-1 flex-wrap">
                                                {webhook.events.map((event) => (
                                                    <Badge key={event} variant="secondary" className="text-xs">
                                                        {event}
                                                    </Badge>
                                                ))}
                                            </div>
                                        </div>
                                        <div className="flex gap-1">
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => testWebhook(webhook.id)}
                                                title="Test webhook"
                                            >
                                                <Send className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => {
                                                    setSelectedWebhook(selectedWebhook === webhook.id ? null : webhook.id);
                                                    if (selectedWebhook !== webhook.id) fetchDeliveries(webhook.id);
                                                }}
                                                title="View history"
                                            >
                                                <Eye className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => deleteWebhook(webhook.id)}
                                                className="text-destructive"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>

                                    {/* Delivery History */}
                                    {selectedWebhook === webhook.id && deliveries[webhook.id] && (
                                        <div className="border-t pt-3 space-y-2">
                                            <p className="text-sm font-medium">Recent Deliveries</p>
                                            {deliveries[webhook.id].length === 0 ? (
                                                <p className="text-xs text-muted-foreground">No deliveries yet</p>
                                            ) : (
                                                <div className="space-y-1">
                                                    {deliveries[webhook.id].slice(0, 5).map((d) => (
                                                        <div key={d.id} className="flex items-center gap-2 text-xs">
                                                            {d.status === 'delivered' ? (
                                                                <CheckCircle className="h-3 w-3 text-emerald-500" />
                                                            ) : d.status === 'failed' ? (
                                                                <XCircle className="h-3 w-3 text-red-500" />
                                                            ) : (
                                                                <Clock className="h-3 w-3 text-amber-500" />
                                                            )}
                                                            <span className="font-mono">{d.event}</span>
                                                            <span className="text-muted-foreground">
                                                                {new Date(d.created_at).toLocaleTimeString()}
                                                            </span>
                                                            {d.response_status && (
                                                                <Badge variant="outline" className="text-[10px]">
                                                                    HTTP {d.response_status}
                                                                </Badge>
                                                            )}
                                                            <span className="text-muted-foreground">
                                                                {d.attempts} attempt{d.attempts !== 1 ? 's' : ''}
                                                            </span>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* Documentation */}
            <Card>
                <CardHeader>
                    <CardTitle className="text-base">Webhook Integration Guide</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                    <p>Your endpoint will receive POST requests with this format:</p>
                    <pre className="bg-muted p-3 rounded-lg overflow-x-auto text-xs">
{`{
  "id": "evt_xxx",
  "type": "order.created",
  "created_at": "2026-04-16T12:00:00Z",
  "data": { ... }
}`}
                    </pre>
                    <p>Verify the signature using HMAC-SHA256:</p>
                    <pre className="bg-muted p-3 rounded-lg overflow-x-auto text-xs">
{`signature = hmac_sha256(payload_json, secret)
expected_header = "sha256=" + signature`}
                    </pre>
                </CardContent>
            </Card>
        </div>
    );
}
